import React, { useEffect, useState } from "react";
import { Button, Col, Form, InputGroup, Row } from "react-bootstrap";
import {
  FaPlus,
  FaTrashAlt,
  FaStore,
  FaPhoneAlt,
  FaUniversity,
  FaRegClock,
  FaCog,
} from "react-icons/fa";
import { useSelector } from "react-redux";

import FieldWarning from "../components/FieldWarning";
import { toast } from "react-toastify";
import {
  useEditOutletDetailsMutation,
  useUploadOutletPhotoMutation,
} from "../slices/OutletSlice";
import imageCompression from "browser-image-compression";
import {
  cardStyle,
  labelStyle,
  Req,
  SectionCard,
  YesNoRadios,
} from "../components/OutletFormKit";

const EditOutletScreen = ({ outlet, editable }) => {
  const { userInfo } = useSelector((state) => state.auth);

  const [outletGroup, setOutletGroup] = useState(outlet?.outletGroup || "");
  const [seperateGst, setSeperateGst] = useState(outlet?.separateGst ?? true);
  const [outletCategory, setOutletCategory] = useState(
    outlet?.outletCategory || "Select Category"
  );
  const [outletName, setOutletName] = useState(outlet?.outletName || "");
  const [outletAddress, setOutletAddress] = useState(
    outlet?.outletAddress || ""
  );
  const [outletBankDetails, setOutletBankDetails] = useState(
    outlet?.outletBankDetails || ""
  );
  const [outletGstNumber, setOutletGstNumber] = useState(
    outlet?.outletGstNumber || ""
  );
  const [outletEmails, setOutletEmails] = useState(
    outlet?.outletEmails?.length ? [...outlet.outletEmails] : [""]
  );
  const [secondaryContact, setSecondaryContact] = useState(
    outlet?.secondaryContact?.length
      ? outlet.secondaryContact.map(String)
      : [""]
  );
  const [outletPincode, setOutletPincode] = useState(
    outlet?.outletPincode != null ? String(outlet.outletPincode) : ""
  );
  const [outletState, setOutletState] = useState(
    outlet?.outletState || "Select State"
  );
  const [outletContact, setOutletContact] = useState(
    outlet?.outletContact != null ? String(outlet.outletContact) : ""
  );
  const [addedBy, setAddedBy] = useState(outlet?.addedBy || "");
  const [operationStatus, setOperationStatus] = useState(
    outlet?.operationStatus ?? true
  );
  const [openingHour, setOpeningHour] = useState("");
  const [closingHour, setClosingHour] = useState("");
  const [openingMinute, setOpeningMinute] = useState("");
  const [closingMinute, setClosingMinute] = useState("");
  const [openedDays, setOpenedDays] = useState(outlet?.openedDays || []);
  const [loginRequired, setLoginRequired] = useState(
    outlet?.loginRequired ?? false
  );
  const [printOnlineOrder, setPrintOnlineOrder] = useState(
    outlet?.printOnlineOrder ?? false
  );
  const [printOutletOrder, setPrintOutletOrder] = useState(
    outlet?.printOutletOrder ?? false
  );
  const [currentlyOpen, setCurrentlyOpen] = useState(
    outlet?.currentlyOpen ?? true
  );
  const [restrictedOutlet, setRestrictedOutlet] = useState(
    outlet?.restrictedOutlet ?? false
  );
  const [wholeSaleOutlet, setWholeSaleOutlet] = useState(
    outlet?.wholeSaleOutlet ?? false
  );
  const [seperatePlatformFee, setSeperatePlatformFee] = useState(
    outlet?.separatePlaformFee ?? false
  );
  const [platformFee, setPlatformFee] = useState(
    outlet?.platformFee != null ? String(outlet.platformFee) : ""
  );

  const [file, setFile] = useState(null);
  const [fileSizeError, setFileSizeError] = useState(false);
  const [fileInvalidError, setFileInvalidError] = useState(false);

  // Field level validation flags
  const [outletNamePresent, setOutletNamePresent] = useState(false);
  const [outletStatePresent, setOutletStatePresent] = useState(false);
  const [outletContactPresent, setOutletContactPresent] = useState(false);
  const [outletAddressPresent, setOutletAddressPresent] = useState(false);
  const [outletPincodePresent, setOutletPincodePresent] = useState(false);
  const [emailErrors, setEmailErrors] = useState([]);
  const [mobileErrors, setMobileErrors] = useState([]);
  const [openTimeError, setOpenTimeError] = useState(false);
  const [closeTimeError, setCloseTimeError] = useState(false);
  const [platformFeeError, setPlatformFeeError] = useState(false);
  const [gstError, setGstError] = useState(false);

  const updateListItem = (setter, index, value) =>
    setter((prev) => prev.map((item, i) => (i === index ? value : item)));

  const addListItem = (setter) => setter((prev) => [...prev, ""]);

  const removeListItem = (setter, index) =>
    setter((prev) => {
      const updated = prev.filter((item, i) => i !== index);
      return updated.length ? updated : [""];
    });

  const hours = [
    "12 AM",
    "01 AM",
    "02 AM",
    "03 AM",
    "04 AM",
    "05 AM",
    "06 AM",
    "07 AM",
    "08 AM",
    "09 AM",
    "10 AM",
    "11 AM",
    "12 PM",
    "01 PM",
    "02 PM",
    "03 PM",
    "04 PM",
    "05 PM",
    "06 PM",
    "07 PM",
    "08 PM",
    "09 PM",
    "10 PM",
    "11 PM",
  ];

  const outCat = [
    "Select Category",
    "Restaurant",
    "Bakery",
    "Food Truck",
    "Theatre",
    "Bars",
    "Hotel",
    "Corporate",
    "Transport",
    "Train",
  ];

  const availableStateNames = [
    "Select State",
    "Andhra Pradesh",
    "Karnataka",
    "Kerala",
    "Maharashtra",
    "Tamil Nadu",
    "Telangana",
  ];
  const daysOfWeek = [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
  ];

  const minuteOptions = Array.from({ length: 60 }, (_, i) =>
    i.toString().padStart(2, "0")
  );

  const handleCheckboxChange = (day) => {
    setOpenedDays((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]
    );
  };

  const [editDetails, { isLoading }] = useEditOutletDetailsMutation();
  const [uploadPhoto, { isLoading: isPhotoUploading }] =
    useUploadOutletPhotoMutation();

  // Prefill opening/closing time from the outlet's decimal hour format
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });

    if (outlet?.openingHour !== undefined && outlet?.openingHour !== null) {
      const hr = Math.floor(outlet.openingHour);
      const min = Math.round((outlet.openingHour % 1) * 100);
      const suffix = hr >= 12 ? "PM" : "AM";
      const hour12 = hr % 12 === 0 ? 12 : hr % 12;
      setOpeningHour(`${hour12.toString().padStart(2, "0")} ${suffix}`);
      setOpeningMinute(min.toString().padStart(2, "0"));
    }

    if (outlet?.closingHour !== undefined && outlet?.closingHour !== null) {
      const hr = Math.floor(outlet.closingHour);
      const min = Math.round((outlet.closingHour % 1) * 100);
      const suffix = hr >= 12 ? "PM" : "AM";
      const hour12 = hr % 12 === 0 ? 12 : hr % 12;
      setClosingHour(`${hour12.toString().padStart(2, "0")} ${suffix}`);
      setClosingMinute(min.toString().padStart(2, "0"));
    }
  }, []);

  // Clear field warnings as soon as the user fixes the value
  useEffect(() => {
    if (outletName?.trim().length > 0) setOutletNamePresent(false);
    if (outletState !== "Select State") setOutletStatePresent(false);
    if (outletAddress?.length >= 10) setOutletAddressPresent(false);
    if (outletPincode?.length === 6) setOutletPincodePresent(false);
    if (outletContact?.length === 10) setOutletContactPresent(false);
    if (openingHour && openingMinute) setOpenTimeError(false);
    if (closingHour && closingMinute) setCloseTimeError(false);
    if (!outletGstNumber.trim() || outletGstNumber.trim().length === 15)
      setGstError(false);
    const pf = String(platformFee ?? "").trim();
    if (pf === "" || (!isNaN(Number(pf)) && Number(pf) <= 100))
      setPlatformFeeError(false);
  }, [
    outletName,
    outletState,
    outletAddress,
    outletPincode,
    outletContact,
    openingHour,
    openingMinute,
    closingHour,
    closingMinute,
    outletGstNumber,
    platformFee,
  ]);

  const verifyInputFields = () => {
    let fieldValidationSuccess = true;

    if (!outletName || outletName.trim().length < 1) {
      setOutletNamePresent(true);
      fieldValidationSuccess = false;
    }

    if (!outletState || outletState === "Select State") {
      setOutletStatePresent(true);
      fieldValidationSuccess = false;
    }

    if (!outletAddress || outletAddress.length < 10) {
      setOutletAddressPresent(true);
      fieldValidationSuccess = false;
    }

    if (String(outletPincode || "").length !== 6) {
      setOutletPincodePresent(true);
      fieldValidationSuccess = false;
    }

    if (String(outletContact || "").length !== 10) {
      setOutletContactPresent(true);
      fieldValidationSuccess = false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const eErrors = outletEmails.map(
      (email) => !!(email.trim() && !emailRegex.test(email.trim()))
    );
    setEmailErrors(eErrors);
    if (eErrors.some(Boolean)) fieldValidationSuccess = false;

    const mErrors = secondaryContact.map(
      (mobile) =>
        !!(
          String(mobile).trim() &&
          !String(mobile)
            .trim()
            .match(/^\d{10,12}$/)
        )
    );
    setMobileErrors(mErrors);
    if (mErrors.some(Boolean)) fieldValidationSuccess = false;

    if (!openingHour || !openingMinute) {
      setOpenTimeError(true);
      fieldValidationSuccess = false;
    }

    if (!closingHour || !closingMinute) {
      setCloseTimeError(true);
      fieldValidationSuccess = false;
    }

    if (outletGstNumber.trim() && outletGstNumber.trim().length !== 15) {
      setGstError(true);
      fieldValidationSuccess = false;
    }

    const pf = String(platformFee ?? "").trim();
    if (pf !== "" && (isNaN(Number(pf)) || Number(pf) > 100)) {
      setPlatformFeeError(true);
      fieldValidationSuccess = false;
    }

    if (!fieldValidationSuccess) {
      toast.error(
        "Some fields have invalid inputs. Please provide valid details."
      );
    }
    return fieldValidationSuccess;
  };

  const submitHandler = async (e) => {
    e.preventDefault();
    if (verifyInputFields()) {
      try {
        const data = {
          outletId: outlet?._id,
          outletGroup: outletGroup,
          separateGst: seperateGst,
          outletCategory: outletCategory,
          outletName: outletName,
          outletAddress: outletAddress,
          outletPincode: outletPincode,
          outletState: outletState,
          outletContact: outletContact,
          addedBy: addedBy,
          operationStatus: operationStatus,
          currentlyOpen: currentlyOpen,
          loginRequired: loginRequired,
          openingHour: Number(
            (openingHour === "12 AM"
              ? 0
              : openingHour?.includes("PM")
              ? Number(openingHour?.split(" ")[0]) +
                (openingHour === "12 PM" ? 0 : 12)
              : Number(openingHour?.split(" ")[0])) +
              "." +
              openingMinute
          ),
          closingHour: Number(
            (closingHour === "12 AM"
              ? 0
              : closingHour?.includes("PM")
              ? Number(closingHour.split(" ")[0]) +
                (closingHour === "12 PM" ? 0 : 12)
              : Number(closingHour.split(" ")[0])) +
              "." +
              closingMinute
          ),
          openedDays: openedDays,
          printOnlineOrder: printOnlineOrder,
          printOutletOrder: printOutletOrder,
          restrictedOutlet: restrictedOutlet,
          wholeSaleOutlet: wholeSaleOutlet,
          separatePlaformFee: seperatePlatformFee,
          platformFee: platformFee,
          outletBankDetails: outletBankDetails,
          outletGstNumber: outletGstNumber,
          outletEmails: outletEmails
            .map((email) => email.trim())
            .filter((email) => email),
          secondaryContact: secondaryContact
            .map((mobile) => String(mobile).trim())
            .filter((mobile) => mobile)
            .map(Number),
          token: userInfo?.token,
        };
        const res = await editDetails(data).unwrap();
        if (res && file) {
          let formData = new FormData();
          formData.append("image", file);
          formData.append("imageKey", `${res._id}`);
          await uploadPhoto({
            token: userInfo.token,
            payload: formData,
          }).unwrap();
        }
        toast.success("Details Updated Successfully");
        editable(false);
      } catch (err) {
        console.error("Error occurred: ", err);
        toast.error(
          err?.data?.error || err?.data?.message || "Something went wrong"
        );
      }
    }
  };

  const handleCancel = () => {
    editable(false);
  };

  const compressImage = async (selectedImage) => {
    const file = selectedImage;
    if (file) {
      try {
        const options = {
          maxSizeMB: 1,
          maxWidthOrHeight: 280,
          useWebWorker: true,
          initialQuality: 1,
        };

        const compressedFile = await imageCompression(file, options);
        const secondOptions = {
          maxSizeMB: 1,
          useWebWorker: true,
          initialQuality: 0.9,
        };

        const secondCompressedFile = await imageCompression(
          compressedFile,
          secondOptions
        );
        setFile(secondCompressedFile);
      } catch (error) {
        console.error("Error processing image:", error);
      }
    }
  };

  const handleFileChange = async (e) => {
    const selectedFile = e?.target?.files[0];
    const dotIndex = selectedFile?.name?.lastIndexOf(".");
    const fileExtension = selectedFile?.name?.slice(dotIndex + 1).toLowerCase();
    let errorPresent = false;
    if (
      !(
        fileExtension === "jpeg" ||
        fileExtension === "jpg" ||
        fileExtension === "png" ||
        fileExtension === "tiff" ||
        fileExtension === "svg" ||
        fileExtension === "jfif"
      )
    ) {
      errorPresent = true;
      setFileInvalidError(true);
      setFile(null);
    } else if (selectedFile?.size / 1000 > 5120) {
      setFileSizeError(true);
      setFile(null);
    } else if (!errorPresent && selectedFile?.size / 1000 <= 5120) {
      setFileSizeError(false);
      setFileInvalidError(false);
      await compressImage(selectedFile);
    }
  };

  return (
    <div
      style={{
        padding: "0.5rem 0.6rem",
        maxWidth: "960px",
        margin: "0 auto",
      }}
    >
      {/* Page header */}
      <div
        style={{
          ...cardStyle,
          padding: "1rem 1.1rem",
          marginBottom: "0.9rem",
        }}
      >
        <h5 className="mb-1">
          <strong style={{ color: "#1d5f91" }}>Edit Outlet</strong>
        </h5>
        <small className="text-muted">
          Update the details of {outlet?.outletName} and submit to save
        </small>
      </div>

      <Form>
        {/* Basic details */}
        <SectionCard
          icon={<FaStore size={15} />}
          title="Basic Details"
          subtitle="Name, category and address of the outlet"
        >
          <Row style={{ rowGap: "1rem" }}>
            <Col xs={12} md={6}>
              <Form.Label style={labelStyle}>
                Outlet Name
                <Req />
              </Form.Label>
              <Form.Control
                type="text"
                value={outletName}
                onChange={(e) => setOutletName(e.target.value)}
                placeholder="Enter outlet name"
              />
              {outletNamePresent && (
                <FieldWarning text="Please enter outlet name" />
              )}
            </Col>
            <Col xs={12} md={6}>
              <Form.Label style={labelStyle}>Outlet Category</Form.Label>
              <Form.Select
                value={outletCategory}
                onChange={(e) => setOutletCategory(e.target.value)}
              >
                {outCat?.map((e) => (
                  <option key={e} value={e}>
                    {e}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col xs={12} md={6}>
              <Form.Label style={labelStyle}>Outlet Group</Form.Label>
              <Form.Control
                type="text"
                value={outletGroup}
                onChange={(e) => setOutletGroup(e.target.value)}
                placeholder="Enter Outlet Group"
              />
            </Col>
            <Col xs={12} md={6}>
              <Form.Label style={labelStyle}>Outlet Image</Form.Label>
              <Form.Control
                type="file"
                accept=".jpeg,.jpg,.png,.tiff,.svg,.jfif"
                onChange={handleFileChange}
              />
              {file && !fileSizeError && !fileInvalidError && (
                <small style={{ color: "#1e7e34" }}>
                  Image selected and ready to upload
                </small>
              )}
              {fileSizeError && (
                <FieldWarning text="Image size should be less than 5 MB, please select a different image" />
              )}
              {fileInvalidError && (
                <FieldWarning text="Only jpeg, jpg, png, tiff and svg formats are supported" />
              )}
            </Col>
            <Col xs={12}>
              <Form.Label style={labelStyle}>
                Outlet Address
                <Req />
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                value={outletAddress}
                onChange={(e) => setOutletAddress(e.target.value)}
                placeholder={
                  "SRI POOJA SNACKS\nNO:8/10, KRISHNARAYAR THEPPAM WEST STREET\nMANI NAGARAM ADURAI -62506\nSTIN: 33BJAPS9693D12"
                }
                style={{ resize: "vertical", whiteSpace: "pre-wrap" }}
              />
              {outletAddressPresent && (
                <FieldWarning text="Address should be atleast 10 chars long" />
              )}
            </Col>
            <Col xs={12} md={6}>
              <Form.Label style={labelStyle}>Added By</Form.Label>
              <Form.Control
                type="text"
                value={addedBy}
                onChange={(e) => {
                  const val = e.target.value.replace(/[^a-zA-Z ]/g, "");
                  setAddedBy(val);
                }}
                placeholder="Enter added by"
              />
            </Col>
          </Row>
        </SectionCard>

        {/* Contact details */}
        <SectionCard
          icon={<FaPhoneAlt size={14} />}
          title="Contact & Location"
          subtitle="Phone numbers, emails and location details"
        >
          <Row style={{ rowGap: "1rem" }}>
            <Col xs={12} md={6}>
              <Form.Label style={labelStyle}>
                Outlet Contact
                <Req />
              </Form.Label>
              <Form.Control
                type="text"
                inputMode="numeric"
                value={outletContact}
                onChange={(e) => {
                  const val = e.currentTarget.value.replace(/[^0-9]/g, "");
                  setOutletContact(val);
                }}
                placeholder="Enter contact number"
              />
              {outletContactPresent && (
                <FieldWarning text="Contact number should be 10 digits" />
              )}
            </Col>
            <Col xs={6} md={3}>
              <Form.Label style={labelStyle}>
                Pincode
                <Req />
              </Form.Label>
              <Form.Control
                type="text"
                inputMode="numeric"
                value={outletPincode}
                onChange={(e) => {
                  const val = e.target.value.replace(/[^0-9]/g, "");
                  setOutletPincode(val);
                }}
                placeholder="Enter Pincode"
              />
              {outletPincodePresent && (
                <FieldWarning text="Pincode should be 6 chars long" />
              )}
            </Col>
            <Col xs={6} md={3}>
              <Form.Label style={labelStyle}>
                State
                <Req />
              </Form.Label>
              <Form.Select
                value={outletState}
                onChange={(e) => setOutletState(e.target.value)}
              >
                {availableStateNames?.map((e) => (
                  <option key={e} value={e}>
                    {e}
                  </option>
                ))}
              </Form.Select>
              {outletStatePresent && (
                <FieldWarning text="Please Select State" />
              )}
            </Col>
            <Col xs={12} md={6}>
              <Form.Label style={labelStyle}>Outlet Emails</Form.Label>
              {outletEmails.map((email, index) => (
                <div className="mb-2" key={index}>
                  <InputGroup>
                    <Form.Control
                      type="email"
                      placeholder="Enter email"
                      value={email}
                      onChange={(e) =>
                        updateListItem(setOutletEmails, index, e.target.value)
                      }
                    />
                    <Button
                      variant="outline-danger"
                      onClick={() => removeListItem(setOutletEmails, index)}
                    >
                      <FaTrashAlt />
                    </Button>
                  </InputGroup>
                  {emailErrors[index] && (
                    <FieldWarning text="Please enter a valid email" />
                  )}
                </div>
              ))}
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => addListItem(setOutletEmails)}
              >
                <FaPlus style={{ marginBottom: "2px" }} /> Add Email
              </Button>
            </Col>
            <Col xs={12} md={6}>
              <Form.Label style={labelStyle}>Secondary Mobiles</Form.Label>
              {secondaryContact.map((mobile, index) => (
                <div className="mb-2" key={index}>
                  <InputGroup>
                    <Form.Control
                      type="text"
                      inputMode="numeric"
                      placeholder="Enter mobile number"
                      value={mobile}
                      onChange={(e) =>
                        updateListItem(
                          setSecondaryContact,
                          index,
                          e.target.value.replace(/[^0-9]/g, "")
                        )
                      }
                    />
                    <Button
                      variant="outline-danger"
                      onClick={() => removeListItem(setSecondaryContact, index)}
                    >
                      <FaTrashAlt />
                    </Button>
                  </InputGroup>
                  {mobileErrors[index] && (
                    <FieldWarning text="Mobile number should be 10 to 12 digits" />
                  )}
                </div>
              ))}
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => addListItem(setSecondaryContact)}
              >
                <FaPlus style={{ marginBottom: "2px" }} /> Add Mobile
              </Button>
            </Col>
          </Row>
        </SectionCard>

        {/* Billing details */}
        <SectionCard
          icon={<FaUniversity size={15} />}
          title="Billing & Bank"
          subtitle="GST, platform fee and bank account details"
        >
          <Row style={{ rowGap: "1rem" }}>
            <Col xs={12} md={6}>
              <Form.Label style={labelStyle}>GST Number</Form.Label>
              <Form.Control
                type="text"
                value={outletGstNumber}
                onChange={(e) =>
                  setOutletGstNumber(e.target.value.toUpperCase())
                }
                placeholder="Enter GST number"
              />
              {gstError && (
                <FieldWarning text="GST number should be 15 characters long" />
              )}
            </Col>
            <Col xs={12} sm={6} md={6}>
              <Form.Label style={labelStyle}>
                Seperate GST ?
                <Req />
              </Form.Label>
              <YesNoRadios
                name="platRadio"
                value={seperateGst}
                onChange={setSeperateGst}
              />
            </Col>
            <Col xs={12} sm={6} md={6}>
              <Form.Label style={labelStyle}>
                Seperate Platform Fee ?
              </Form.Label>
              <YesNoRadios
                name="platformRadio"
                value={seperatePlatformFee}
                onChange={setSeperatePlatformFee}
              />
            </Col>
            {/* <Col xs={12} sm={6} md={6}>
              <Form.Label style={labelStyle}>Platform Fee %</Form.Label>
              <Form.Control
                type="text"
                inputMode="numeric"
                value={platformFee}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^0-9]/g, "");
                  setPlatformFee(value);
                }}
                placeholder="Enter Platformfee"
              />
              {platformFeeError && (
                <FieldWarning text="Platform fee should be between 0 and 100" />
              )}
            </Col> */}
            <Col xs={12}>
              <Form.Label style={labelStyle}>Bank Account Details</Form.Label>
              <Form.Control
                as="textarea"
                rows={5}
                value={outletBankDetails}
                onChange={(e) => setOutletBankDetails(e.target.value)}
                placeholder={
                  "Bank Name : XYZ Bank\nAccount Number : 23423435\nAccount Name : David\nIFSC code : 43534543"
                }
                style={{ resize: "vertical", whiteSpace: "pre-wrap" }}
              />
            </Col>
          </Row>
        </SectionCard>

        {/* Operating hours */}
        <SectionCard
          icon={<FaRegClock size={15} />}
          title="Operating Hours"
          subtitle="When the outlet is open for business"
        >
          <Row style={{ rowGap: "1rem" }}>
            <Col xs={12} sm={6}>
              <Form.Label style={labelStyle}>
                In Operation Now ?
                <Req />
              </Form.Label>
              <YesNoRadios
                name="operationStatus"
                value={operationStatus}
                onChange={setOperationStatus}
              />
            </Col>
            <Col xs={12} sm={6}>
              <Form.Label style={labelStyle}>Currently Open ?</Form.Label>
              <YesNoRadios
                name="currentlyOpen"
                value={currentlyOpen}
                onChange={setCurrentlyOpen}
              />
            </Col>
            <Col xs={12} sm={6}>
              <Form.Label style={labelStyle}>
                Opening Time (HH:MM)
                <Req />
              </Form.Label>
              <div className="d-flex" style={{ gap: "0.5rem" }}>
                <Form.Select
                  value={openingHour}
                  onChange={(e) => setOpeningHour(e.target.value)}
                >
                  <option value="">Select Hour</option>
                  {hours.map((hour) => (
                    <option key={hour} value={hour}>
                      {hour}
                    </option>
                  ))}
                </Form.Select>
                <Form.Select
                  value={openingMinute}
                  onChange={(e) => setOpeningMinute(e.target.value)}
                >
                  <option value="">Select Minutes</option>
                  {minuteOptions.map((mins) => (
                    <option key={mins} value={mins}>
                      {mins}
                    </option>
                  ))}
                </Form.Select>
              </div>
              {openTimeError && (
                <FieldWarning text="Please select opening hour and minutes" />
              )}
            </Col>
            <Col xs={12} sm={6}>
              <Form.Label style={labelStyle}>
                Closing Time (HH:MM)
                <Req />
              </Form.Label>
              <div className="d-flex" style={{ gap: "0.5rem" }}>
                <Form.Select
                  value={closingHour}
                  onChange={(e) => setClosingHour(e.target.value)}
                >
                  <option value="">Select Hour</option>
                  {hours.map((hour) => (
                    <option key={hour} value={hour}>
                      {hour}
                    </option>
                  ))}
                </Form.Select>
                <Form.Select
                  value={closingMinute}
                  onChange={(e) => setClosingMinute(e.target.value)}
                >
                  <option value="">Select Minutes</option>
                  {minuteOptions.map((mins) => (
                    <option key={mins} value={mins}>
                      {mins}
                    </option>
                  ))}
                </Form.Select>
              </div>
              {closeTimeError && (
                <FieldWarning text="Please select closing hour and minutes" />
              )}
            </Col>
            <Col xs={12}>
              <Form.Label style={labelStyle}>Opened Days</Form.Label>
              <Row>
                {daysOfWeek.map((day) => (
                  <Col key={day} xs={6} sm={4} md={3} className="mb-2">
                    <Form.Check
                      type="checkbox"
                      id={`day-${day}`}
                      label={day}
                      checked={openedDays.includes(day)}
                      onChange={() => handleCheckboxChange(day)}
                    />
                  </Col>
                ))}
              </Row>
            </Col>
          </Row>
        </SectionCard>

        {/* Preferences */}
        <SectionCard
          icon={<FaCog size={15} />}
          title="Preferences"
          subtitle="Access and printing preferences"
        >
          <Row style={{ rowGap: "1rem" }}>
            <Col xs={12} sm={6}>
              <Form.Label style={labelStyle}>Restricted Outlet ?</Form.Label>
              <YesNoRadios
                name="restrictedOutlet"
                value={restrictedOutlet}
                onChange={setRestrictedOutlet}
              />
            </Col>
            <Col xs={12} sm={6}>
              <Form.Label style={labelStyle}>Wholesale Outlet ?</Form.Label>
              <YesNoRadios
                name="wholeSaleOutlet"
                value={wholeSaleOutlet}
                onChange={setWholeSaleOutlet}
              />
            </Col>
            <Col xs={12} sm={6}>
              <Form.Label style={labelStyle}>Login Required ?</Form.Label>
              <YesNoRadios
                name="loginRequired"
                value={loginRequired}
                onChange={setLoginRequired}
              />
            </Col>
            <Col xs={12} sm={6}>
              <Form.Label style={labelStyle}>
                Automatically Print Online Orders ?
              </Form.Label>
              <YesNoRadios
                name="printOnlineOrder"
                value={printOnlineOrder}
                onChange={setPrintOnlineOrder}
              />
            </Col>
            <Col xs={12} sm={6}>
              <Form.Label style={labelStyle}>
                Automatically Print Outlet Orders ?
              </Form.Label>
              <YesNoRadios
                name="printOutletOrder"
                value={printOutletOrder}
                onChange={setPrintOutletOrder}
              />
            </Col>
          </Row>
        </SectionCard>

        {/* Submit / Cancel */}
        <div
          style={{
            ...cardStyle,
            padding: "0.9rem 1rem",
            marginBottom: "1.5rem",
          }}
        >
          <Row className="justify-content-center" style={{ rowGap: "0.6rem" }}>
            <Col xs={12} sm={5} md={4} className="d-grid">
              <Button
                className={`${userInfo?.themeColor}ThemeHeaderTop`}
                style={{
                  borderRadius: "8px",
                  fontWeight: "600",
                  padding: "0.6rem",
                }}
                onClick={submitHandler}
                disabled={isLoading || isPhotoUploading}
              >
                {isLoading || isPhotoUploading
                  ? "Submitting..."
                  : "Update Outlet"}
              </Button>
            </Col>
            <Col xs={12} sm={5} md={4} className="d-grid">
              <Button
                variant="outline-secondary"
                style={{
                  borderRadius: "8px",
                  fontWeight: "600",
                  padding: "0.6rem",
                }}
                onClick={handleCancel}
                disabled={isLoading || isPhotoUploading}
              >
                Cancel
              </Button>
            </Col>
          </Row>
        </div>
      </Form>
    </div>
  );
};

export default EditOutletScreen;
